/**
 * Shrinking a photo before it leaves the browser.
 *
 * A phone camera hands you a four-megabyte, four-thousand-pixel photo; a menu
 * tile shows it at a couple of hundred pixels. Sending the original wastes the
 * shop's upload, the server's disk, and every till and phone that later loads
 * it. So the browser draws it small onto a canvas and sends that instead —
 * typically well under a hundred kilobytes, and no server-side image library
 * to install or keep patched.
 *
 * The longest side is capped rather than both, so a tall photo and a wide one
 * both come out sharp at the size a tile actually needs. EXIF orientation is
 * left to the browser, which now applies it when drawing to a canvas.
 */

const MAX_SIDE = 900; // a tile is ~200px; this stays crisp on a retina screen
const QUALITY = 0.82; // JPEG; the seam where more bytes stop buying visible detail

/**
 * @param {File} file  a JPG, PNG or WebP the person chose
 * @returns {Promise<Blob>} a JPEG small enough to send as-is
 */
export function shrinkImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);

      const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const width = Math.round(img.width * scale);
      const height = Math.round(img.height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('This browser cannot resize the image.'));
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('That image could not be read.'))),
        'image/jpeg',
        QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file is not an image the browser can open.'));
    };

    img.src = url;
  });
}
